import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { addDays, isSameDay, isWeekend } from 'date-fns';
import { useMemo } from 'react';
import { DatePicker } from '../../../../common/react-hook-form/components/DatePicker.js';
import { type Choices } from '../../OpenFormAnswers.js';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { useOpenFormPublicHolidays } from '../../OpenFormHooks/useOpenFormPublicHolidays.js';
import { getValue } from '../../OpenFormUtils.js';

const getDatesWithOffset = (offset: number, blockedDates: Date[]) => {
  let today = new Date();
  let amountOfBlocked = 0;

  // We add `offset` amount of weekdays into the blockedDates array
  while (amountOfBlocked < offset) {
    if (!isWeekend(today)) {
      blockedDates.push(today);
      amountOfBlocked++;
    }
    today = addDays(today, 1);
  }

  // Move `today` to the next non-{weekend,blocked} date
  while (isWeekend(today) || blockedDates.some(date => isSameDay(date, today))) {
    today = addDays(today, 1);
  }

  return { blockedDates, today };
};

export const OpenFormQuestionDate = ({
  disabled,
  required,
  label,
  name,
  offset = 0,
  choices,
  infoText,
}: {
  disabled: boolean;
  required: boolean;
  label: string;
  name: string;
  offset?: number;
  choices: Choices | undefined;
  infoText?: string;
}) => {
  const holidays = useOpenFormPublicHolidays();
  const { blockedDates, today } = useMemo(() => getDatesWithOffset(offset, holidays), [offset, holidays]);
  return (
    <OpenFormGridRow>
      <OpenFormGridCol colWidth={6} className={classNames({ ['label--mandatory']: required })}>
        {disabled ? (
          <CL.Input disabled={disabled} value={getValue(choices)} />
        ) : (
          <div className="of-openform__view__question-date-wrapper">
            <DatePicker
              required={required}
              label={label}
              name={name}
              blockedDates={blockedDates}
              defaultValue={getValue(choices)}
              disableAllPastDays={true}
              disablePreviousMonths={true}
              disableWeekends={true}
              forcePad={true}
              today={today}
            />
            {infoText && (
              <CL.Popover triggerElement={<CL.Icon icon="information" size="m" color="blue-600" />} placement="top">
                {infoText}
              </CL.Popover>
            )}
          </div>
        )}
      </OpenFormGridCol>
    </OpenFormGridRow>
  );
};
